
import React from "react";
import { Helmet } from "react-helmet";

interface HeadMetaProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://elementalvalue.co";
const DEFAULT_TITLE = "Elemental Value - Orden financiero y criterio estratégico";
const DEFAULT_DESCRIPTION = "Transformamos datos financieros en conversaciones estratégicas. Servicios de CFO externo, reporting y análisis para empresas que quieren tomar mejores decisiones.";

const HeadMeta = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = "/",
  noindex = false,
  jsonLd,
}: HeadMetaProps) => {
  const handleStyleLoad = (event: React.SyntheticEvent<HTMLLinkElement>) => {
    event.currentTarget.media = 'all';
  };

  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang="es" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="keywords" content="CFO externo, dirección financiera, reporting financiero, análisis de datos, gestión financiera, consultoría financiera, Barcelona" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Elemental Value" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {/* Load critical resources first with priority hints */}
      <link rel="icon" type="image/x-icon" href="/img/favicon.png" />
      <link rel="preconnect" href="https://cdnjs.cloudflare.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://cdnjs.cloudflare.com" />
      <link rel="preconnect" href="https://unpkg.com" crossOrigin="anonymous" />

      {/* Add resource hints for font domains */}
      <link rel="preload" href="/fonts/aeo-r.ttf" as="font" type="font/ttf" crossOrigin="anonymous" />
      <link rel="preload" href="/fonts/fd-l.otf" as="font" type="font/otf" crossOrigin="anonymous" />

      {/* Preload critical images */}
      <link rel="preload" href="/img/logo.svg" as="image" fetchPriority="high" />
      <link rel="preload" href="/img/co.svg" as="image" fetchPriority="high" />
      <link rel="preload" href="/img/ev.svg" as="image" fetchPriority="high" />

      {/* Load CSS with highest priority */}
      <link rel="stylesheet" href="/src/swiper.min.css" media="print" onLoad={handleStyleLoad} />
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css"
        media="print"
        onLoad={handleStyleLoad}
      />

      {/* Load JS */}
      <script src="/src/swiper.min.js" defer data-critical="true"></script>
      <script type="text/javascript" src="https://unpkg.com/typewriter-effect@latest/dist/core.js" defer></script>
      <script src="https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js" defer></script>
      <script src="/src/wow.js" type="text/javascript" defer data-critical="true"></script>

      <link rel="modulepreload" href="/src/main.tsx" />

      <meta httpEquiv="resource-type" content="document" />
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000, immutable" />

      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
};

export default HeadMeta;
